import * as React from 'react';
import {
    CommandBarButton,
    ContextualMenuItemType,
    IButtonStyles,
    IContextualMenuItem,
    IContextualMenuProps,
} from '@fluentui/react';
import { LanguageContext } from '../../../../utilities/LocalizationModule';
import { ISortOption } from '../../interfaces/ISortOption';
import { FileSortOptionKeys } from '../../config/constants';

const filterButtonStyles: IButtonStyles = {
    root: {
        minWidth: 24,
        maxWidth: 24,
        height: 24,
        marginRight: -5,
        ":hover": {
            color: "#323130"
        }
    },
    icon: {
        color: "#323130"
    },
};

export interface IFileSortOptionsProps {
    addRelevanceOption: boolean;
    onSortApplied: (sortOption: ISortOption) => void;
}

export const FileSortOptions: React.FunctionComponent<IFileSortOptionsProps> = ({ addRelevanceOption, onSortApplied }: IFileSortOptionsProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [sortField, setSortField] = React.useState<string>(!addRelevanceOption ? FileSortOptionKeys.SortFields.TimeLastModified : FileSortOptionKeys.SortFields.Relevance);
    const [sortOrder, setSortOrder] = React.useState<string>(FileSortOptionKeys.SortOrder.Descending);
    const [sortOrderAscendingText, setSortOrderAscendingText] = React.useState<string>(!addRelevanceOption ? languageStrings.OldestOnTop : languageStrings.LeastRelevantOnTop);
    const [sortOrderDescendingText, setSortOrderDescendingText] = React.useState<string>(!addRelevanceOption ? languageStrings.NewestOnTop: languageStrings.MostRelevantOnTop);

    const onToggleSortField = React.useCallback(
        (ev?: React.MouseEvent<HTMLButtonElement>, item?: IContextualMenuItem): void => {
            if (item) {
                setSortField(item.key);

                if (item.key === FileSortOptionKeys.SortFields.Size) {
                    setSortOrderAscendingText(languageStrings.SmallestOnTop);
                    setSortOrderDescendingText(languageStrings.LargestOnTop);
                }
                else if (item.key === FileSortOptionKeys.SortFields.TimeLastModified || item.key === FileSortOptionKeys.SortFields.TimeCreated) {
                    setSortOrderAscendingText(languageStrings.OldestOnTop);
                    setSortOrderDescendingText(languageStrings.NewestOnTop);
                }
                else {
                    setSortOrderAscendingText(languageStrings.Ascending);
                    setSortOrderDescendingText(languageStrings.Descending);
                }

                onSortApplied({
                    sortField: item.key,
                    sortOrder: sortOrder
                });
            }
            else {
                ev && ev.preventDefault();
            }
        },
        [sortOrder, setSortField, setSortOrderAscendingText, setSortOrderDescendingText, onSortApplied]
    );

    const onToggleSortOrder = React.useCallback(
        (ev?: React.MouseEvent<HTMLButtonElement>, item?: IContextualMenuItem): void => {
            if (item) {
                setSortOrder(item.key);

                onSortApplied({
                    sortField: sortField,
                    sortOrder: item.key
                });
            }
            else {
                ev && ev.preventDefault();
            }
        },
        [sortField, setSortOrder, onSortApplied]
    );

    const menuProps: IContextualMenuProps = React.useMemo(
        () => ({
            isBeakVisible: false,
            shouldFocusOnMount: true,
            items: [
                {
                    key: 'SortField',
                    itemType: ContextualMenuItemType.Section,
                    sectionProps: {
                        bottomDivider: true,
                        title: languageStrings.SortBy,
                        items: [
                            (addRelevanceOption && { key: FileSortOptionKeys.SortFields.Relevance, text: languageStrings.Relevance, canCheck: true, isChecked: sortField === FileSortOptionKeys.SortFields.Relevance, onClick: onToggleSortField }),
                            { key: FileSortOptionKeys.SortFields.Name, text: languageStrings.Name, canCheck: true, isChecked: sortField === FileSortOptionKeys.SortFields.Name, onClick: onToggleSortField },
                            { key: FileSortOptionKeys.SortFields.TimeLastModified, text: languageStrings.Modified, canCheck: true, isChecked: sortField === FileSortOptionKeys.SortFields.TimeLastModified, onClick: onToggleSortField },
                            { key: FileSortOptionKeys.SortFields.TimeCreated, text: languageStrings.Created, canCheck: true, isChecked: sortField === FileSortOptionKeys.SortFields.TimeCreated, onClick: onToggleSortField },
                            { key: FileSortOptionKeys.SortFields.Type, text: languageStrings.Type, canCheck: true, isChecked: sortField === FileSortOptionKeys.SortFields.Type, onClick: onToggleSortField },
                            { key: FileSortOptionKeys.SortFields.Size, text: languageStrings.Size, canCheck: true, isChecked: sortField === FileSortOptionKeys.SortFields.Size, onClick: onToggleSortField },
                        ]
                        .filter(item => item),
                    },
                },
                {
                    key: 'SortOrder',
                    itemType: ContextualMenuItemType.Section,
                    sectionProps: {
                        topDivider: true,
                        bottomDivider: true,
                        title: languageStrings.SortOrder,
                        items: [
                            { key: FileSortOptionKeys.SortOrder.Ascending, text: sortOrderAscendingText, canCheck: true, isChecked: sortOrder === FileSortOptionKeys.SortOrder.Ascending, onClick: onToggleSortOrder },
                            { key: FileSortOptionKeys.SortOrder.Descending, text: sortOrderDescendingText, canCheck: true, isChecked: sortOrder === FileSortOptionKeys.SortOrder.Descending, onClick: onToggleSortOrder },
                        ],
                    },
                },
            ],
        }),
        [sortField, sortOrder, onToggleSortField, onToggleSortOrder]
    );

    return <CommandBarButton
        styles={filterButtonStyles}
        iconProps={{ iconName: "Sort" }}
        onRenderMenuIcon={() => null}
        menuProps={menuProps} />
};